<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { buildMentionToken } from './mentionTokens';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  enableWorkspace: { type: Boolean, default: true },
  enableTeams: { type: Boolean, default: true },
  enableAgents: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const store = useStore();
const accountAgents = useMapGetter('agents/getVerifiedAgents');
const accountTeams = useMapGetter('teams/getTeams');

const showMenu = ref(false);
const mentionQuery = ref('');
const mentionRange = ref({ start: 0, end: 0 });
const selectedIndex = ref(0);
const textareaWrapperRef = ref(null);

const mentionItems = computed(() => {
  const query = mentionQuery.value.trim().toLowerCase();
  const items = [];

  if (props.enableWorkspace) {
    const label = t('AI_AGENTS.MENTIONS.WORKSPACE');
    if (
      !query ||
      label.toLowerCase().includes(query) ||
      'workspace'.includes(query)
    ) {
      items.push({
        key: 'workspace',
        type: 'workspace',
        id: 'workspace',
        label,
        description: '{{@workspace}}',
      });
    }
  }

  if (props.enableTeams) {
    (accountTeams.value || []).forEach(team => {
      const label = team.name;
      if (query && !label.toLowerCase().includes(query)) return;
      items.push({
        key: `team-${team.id}`,
        type: 'team',
        id: team.id,
        label,
        description: `{{@team.${team.id}}}`,
      });
    });
  }

  if (props.enableAgents) {
    (accountAgents.value || []).forEach(agent => {
      const label = agent.name || agent.available_name;
      if (query && !label.toLowerCase().includes(query)) return;
      items.push({
        key: `agent-${agent.id}`,
        type: 'agent',
        id: agent.id,
        label,
        description: `{{@agent.${agent.id}}}`,
      });
    });
  }

  return items;
});

const detectMention = (value, cursor) => {
  const before = value.slice(0, cursor);
  const match = before.match(/@([\w.]*)$/);
  if (!match) {
    showMenu.value = false;
    return;
  }

  mentionQuery.value = match[1] || '';
  mentionRange.value = {
    start: cursor - match[0].length,
    end: cursor,
  };
  showMenu.value = true;
};

const handleInput = value => {
  emit('update:modelValue', value);
  nextTick(() => {
    const textarea = textareaWrapperRef.value?.querySelector('textarea');
    if (!textarea) return;
    detectMention(value, textarea.selectionStart);
  });
};

const insertMention = item => {
  const token = buildMentionToken(item);
  const { start, end } = mentionRange.value;
  const nextValue = `${props.modelValue.slice(0, start)}${token} ${props.modelValue.slice(end)}`;
  emit('update:modelValue', nextValue);
  showMenu.value = false;
  mentionQuery.value = '';
};

const onSelect = index => {
  const item = mentionItems.value[index];
  if (!item) return;
  insertMention(item);
};

useKeyboardNavigableList({
  items: mentionItems,
  onSelect,
  selectedIndex,
});

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});
</script>

<template>
  <div ref="textareaWrapperRef" class="relative grid gap-1">
    <TextArea
      :model-value="modelValue"
      :label="label"
      :placeholder="placeholder"
      auto-height
      min-height="4rem"
      max-height="12rem"
      :max-length="2000"
      :show-character-count="false"
      @update:model-value="handleInput"
      @focus="event => detectMention(modelValue, event.target.selectionStart)"
    />
    <p class="m-0 text-xs text-n-slate-11">
      {{ t('AI_AGENTS.MENTIONS.HINT') }}
    </p>
    <div
      v-if="showMenu && mentionItems.length"
      class="absolute bottom-full left-0 z-20 mb-1 w-full max-h-48 overflow-y-auto rounded-xl border border-n-strong bg-n-solid-1 p-1 shadow-md"
    >
      <button
        v-for="(item, index) in mentionItems"
        :key="item.key"
        type="button"
        class="flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left hover:bg-n-alpha-black2"
        :class="{ 'bg-n-alpha-black2': index === selectedIndex }"
        @mousedown.prevent="insertMention(item)"
      >
        <span class="text-sm font-medium text-n-slate-12">{{
          item.label
        }}</span>
        <span class="text-xs text-n-slate-11">{{ item.description }}</span>
      </button>
    </div>
  </div>
</template>
